import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  CurrencyDollar,
  EmptyState,
  MarkerPin01,
  MessageTextSquare01,
  Metric,
  PageHeader,
  ProgressBar,
  Ticket01,
  Tooltip,
  Users01,
} from '@opendate-app/setlist';

const percent = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

const STATUS_BADGES = {
  upcoming: { color: 'warning', label: 'Upcoming' },
  ended: { color: 'gray', label: 'Ended' },
};

const statusBadge = (event) => {
  if (event.status === 'in_progress') {
    return event.isPublic ? { color: 'success', label: 'Open now' } : { color: 'gray', label: 'Active' };
  }
  return STATUS_BADGES[event.status];
};

const mutedText = { color: 'var(--sl-text-tertiary)' };
const rowBorder = { borderTop: '1px solid var(--sl-border-secondary)' };
const cardBorder = { '--setlist-card-border': 'var(--sl-border-secondary)' };

const SummaryMetrics = ({ summary, isToday, formatMoney }) => {
  const revenueChange = summary.revenueSameDayLastWeek > 0
    ? Math.round(((summary.revenue - summary.revenueSameDayLastWeek) / summary.revenueSameDayLastWeek) * 100)
    : null;

  const metrics = [
    {
      label: 'Expected guests',
      value: summary.expectedGuests.toLocaleString(),
      icon: Users01,
      footer: `across ${summary.publicEventCount} public event${summary.publicEventCount === 1 ? '' : 's'}`,
    },
    {
      label: isToday ? 'Revenue today' : 'Event revenue',
      value: formatMoney(summary.revenue),
      icon: CurrencyDollar,
      footer: isToday ? undefined : 'ticket sales to date',
      change: revenueChange === null ? undefined : {
        value: `${Math.abs(revenueChange)}%`,
        direction: revenueChange > 0 ? 'up' : revenueChange < 0 ? 'down' : 'flat',
        label: 'vs. same day last week',
      },
    },
    {
      label: 'Tickets sold',
      value: summary.ticketsSold.toLocaleString(),
      icon: Ticket01,
      footer: `${summary.capacity.toLocaleString()} total capacity`,
    },
    {
      label: 'Checked in so far',
      value: summary.checkedIn.toLocaleString(),
      icon: CheckCircle,
      footer: `${percent(summary.checkedIn, summary.ticketsSold)}% of today's tickets`,
    },
  ];

  return (
    <div className="row mx-n2">
      {metrics.map((metric) => (
        <div key={metric.label} className="col-md-6 col-xl-3 px-2 mb-3 d-flex flex-column">
          <Metric
            className="flex-grow-1"
            label={metric.label}
            value={metric.value}
            icon={metric.icon}
            change={metric.change}
            footer={metric.footer && <span style={mutedText}>{metric.footer}</span>}
          />
        </div>
      ))}
    </div>
  );
};

const ScheduleRow = ({ event, isFirst, formatMoney }) => {
  const badge = statusBadge(event);

  return (
    <div className="d-flex px-4 py-3" style={isFirst ? undefined : rowBorder}>
      <div style={{ width: 96, flexShrink: 0 }}>
        <div className="font-weight-bold">{event.startTime}</div>
        {event.endTime && <div style={mutedText}>–{event.endTime}</div>}
      </div>
      <div className="flex-grow-1 pl-3" style={{ minWidth: 0 }}>
        <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
          <a href={event.path} className="font-weight-bold" style={{ color: 'var(--sl-text-primary)' }}>{event.title}</a>
          {event.tags.map((tag) => <Badge key={tag} color="brand" size="sm">{tag}</Badge>)}
        </div>
        <div style={mutedText}>
          <MarkerPin01 width={14} height={14} className="mr-1" />
          {event.venueName}
        </div>
        {event.isPublic ? (
          <div className="mt-2">
            <div className="d-flex justify-content-between" style={mutedText}>
              <span>
                <strong style={{ color: 'var(--sl-text-primary)' }}>{event.ticketsSold.toLocaleString()}</strong>
                {` / ${event.capacity.toLocaleString()} tickets`}
              </span>
              <span>{percent(event.ticketsSold, event.capacity)}%</span>
            </div>
            <ProgressBar value={percent(event.ticketsSold, event.capacity)} label="none" />
          </div>
        ) : (
          <div className="font-italic" style={mutedText}>Private event — no public tickets</div>
        )}
      </div>
      <div className="text-right pl-3" style={{ flexShrink: 0 }}>
        {badge && <Badge color={badge.color} size="sm" dot>{badge.label}</Badge>}
        <div className="font-weight-bold mt-2">{formatMoney(event.revenue)}</div>
        <div style={mutedText}>revenue</div>
      </div>
    </div>
  );
};

const DoorActivityRow = ({ event, isFirst }) => {
  let detail;
  if (!event.isPublic) {
    detail = <span className="font-italic" style={mutedText}>Private event</span>;
  } else if (!event.doorsOpen) {
    detail = <span style={mutedText}>Doors open at {event.doorTime}</span>;
  } else {
    detail = (
      <span style={mutedText}>
        <strong style={{ color: 'var(--sl-text-primary)' }}>{event.checkedIn.toLocaleString()}</strong>
        {` / ${event.ticketsSold.toLocaleString()} scanned `}
        <Badge color="success" size="sm">{percent(event.checkedIn, event.ticketsSold)}%</Badge>
      </span>
    );
  }

  return (
    <div className="px-4 py-3" style={isFirst ? undefined : rowBorder}>
      <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
        <span className="font-weight-bold">{event.title}</span>
        {detail}
      </div>
      {event.isPublic && event.doorsOpen && (
        <div className="mt-2">
          <ProgressBar value={percent(event.checkedIn, event.ticketsSold)} label="none" />
        </div>
      )}
    </div>
  );
};

const RecentScans = ({ scans }) => (
  <div className="px-4 py-3" style={rowBorder}>
    <div className="small font-weight-bold text-uppercase mb-2" style={mutedText}>Recent scans</div>
    {scans.map((scan) => (
      <div key={scan.id} className="d-flex justify-content-between py-1">
        <span>
          {scan.ticketTypeName || 'Ticket'}
          <span style={mutedText}>{` · ${scan.eventTitle}`}</span>
        </span>
        <span style={mutedText}>{scan.scannedAt}</span>
      </div>
    ))}
  </div>
);

const WeatherCard = ({ weather }) => (
  <Card header={<CardHeader title="Weather" />}>
    {weather.map((forecast) => (
      <div key={forecast.location} className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
        <i className={`fas fa-${forecast.icon} fa-2x`} style={mutedText} aria-hidden="true" />
        <div>
          <div className="font-weight-bold">{forecast.location}</div>
          <div style={mutedText}>
            {`${forecast.condition} · High ${forecast.highTemp}° / Low ${forecast.lowTemp}°`}
          </div>
        </div>
      </div>
    ))}
  </Card>
);

const NotesCard = ({ notes }) => (
  <Card header={<CardHeader title="Notes & notices" />} padding="none">
    {notes.length === 0 ? (
      <div className="p-4" style={mutedText}>No notes on this day's events.</div>
    ) : notes.map((note, index) => (
      <div key={note.id} className="px-4 py-3" style={index > 0 ? rowBorder : undefined}>
        {note.title && <div className="font-weight-bold">{note.title}</div>}
        <div style={{ overflowWrap: 'anywhere' }}>{note.body}</div>
        <div className="small mt-1" style={mutedText}>
          {`${note.authorName} · ${note.createdAt} · `}
          <a href={note.eventPath}>{note.eventTitle}</a>
        </div>
      </div>
    ))}
  </Card>
);

const DEPARTMENT_COLORS = ['#2E90FA', '#7A5AF8', '#EF6820', '#15B79E', '#EE46BC', '#EAAA08', '#6172F3', '#66C61C'];
const OPEN_SHIFT_COLOR = '#98A2B3';
const NAME_COLUMN_WIDTH = 232;
const HOUR_WIDTH = 72;
const BAR_HEIGHT = 28;

const STAFF_STATUS_BADGES = {
  late: { color: 'warning', label: 'Late' },
  no_show: { color: 'error', label: 'No-show' },
};

const departmentColors = (shifts) => {
  const departments = [...new Set(shifts.filter((s) => !s.open && s.department).map((s) => s.department))].sort();
  const colors = new Map(departments.map((d, i) => [d, DEPARTMENT_COLORS[i % DEPARTMENT_COLORS.length]]));
  return { departments, colorFor: (department) => colors.get(department) || OPEN_SHIFT_COLOR };
};

const visuallyHidden = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
};

const hourLabel = (hour) => {
  const h = ((hour % 24) + 24) % 24;
  const suffix = h < 12 ? 'a' : 'p';
  return `${h % 12 === 0 ? 12 : h % 12}${suffix}`;
};

const wallClockHour = (now, date, timeZone) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date(now)).map((p) => [p.type, p.value]));
  const dayOffset = (Date.UTC(+parts.year, +parts.month - 1, +parts.day) - Date.parse(`${date}T00:00:00Z`)) / 86400000;
  return Number(parts.hour) + Number(parts.minute) / 60 + 24 * dayOffset;
};

const useNow = (enabled) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(id);
  }, [enabled]);
  return now;
};

const staffRows = (shifts) => {
  const rows = new Map();
  shifts.forEach((shift) => {
    const key = shift.open || !shift.personId ? `shift:${shift.id}` : `person:${shift.personId}`;
    if (!rows.has(key)) rows.set(key, { key, name: shift.name, initials: shift.initials, open: shift.open, shifts: [] });
    rows.get(key).shifts.push(shift);
  });
  return [...rows.values()];
};

const shiftDescription = (shift) => [
  shift.open ? 'Open shift' : shift.name,
  shift.role,
  shift.department !== shift.role ? shift.department : null,
  `${shift.startLabel}–${shift.endLabel}`,
  STAFF_STATUS_BADGES[shift.status]?.label,
  shift.location,
  shift.note ? `note: ${shift.note}` : null,
].filter(Boolean).join(', ');

const ShiftBar = ({ shift, firstHour, onNow, colorFor }) => {
  const color = shift.open ? OPEN_SHIFT_COLOR : colorFor(shift.department);
  const width = Math.max((shift.endHour - shift.startHour) * HOUR_WIDTH, 24);
  const label = shift.role || shift.department || (shift.open ? 'Open shift' : 'Shift');
  const title = [shift.role, shift.department].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') || label;

  return (
    <div className="position-absolute" style={{ left: (shift.startHour - firstHour) * HOUR_WIDTH, width, top: (40 - BAR_HEIGHT) / 2 }}>
      <Tooltip
        title={`${title} · ${shift.startLabel}–${shift.endLabel}`}
        supportingText={[shift.location, shift.note].filter(Boolean).join(' — ') || undefined}
      >
        <div
          role="img"
          tabIndex={0}
          aria-label={`${shiftDescription(shift)}${onNow ? ', on now' : ''}`}
          data-testid="staffing-shift"
          data-on-now={onNow ? 'true' : 'false'}
          className="d-flex align-items-center px-2"
          style={{
            width,
            height: BAR_HEIGHT,
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            color: 'var(--sl-text-primary)',
            background: `${color}${shift.open ? '14' : '29'}`,
            border: `1px ${shift.open ? 'dashed' : 'solid'} ${color}`,
            borderLeftWidth: 4,
            boxShadow: onNow ? `0 0 0 2px ${color}55, 0 0 14px 2px ${color}99` : undefined,
            transition: 'box-shadow 300ms ease',
            gap: 6,
          }}
        >
          {onNow && <span aria-hidden style={{ width: 8, height: 8, borderRadius: 4, background: color, flexShrink: 0 }} />}
          <span className="text-truncate" aria-hidden>{label}</span>
          {shift.note && <MessageTextSquare01 width={12} height={12} style={{ flexShrink: 0 }} aria-hidden />}
        </div>
      </Tooltip>
    </div>
  );
};

const StaffingLegend = ({ departments, colorFor }) => {
  if (departments.length < 2) return null;
  return (
    <div className="d-flex flex-wrap px-4 py-2" style={{ gap: 12, fontSize: 12, ...mutedText, ...rowBorder }} data-testid="staffing-legend">
      {departments.map((department) => (
        <span key={department} className="d-inline-flex align-items-center" style={{ gap: 6 }}>
          <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: colorFor(department) }} />
          {department}
        </span>
      ))}
    </div>
  );
};

const StaffingFootnotes = ({ unavailableLocations, pendingLocations }) => (
  <>
    {pendingLocations.length > 0 && (
      <div className="px-4 py-2" style={{ ...mutedText, ...rowBorder, fontSize: 12 }} data-testid="staffing-pending">
        {`Still syncing shifts from Homebase for ${pendingLocations.join(', ')}.`}
      </div>
    )}
    {unavailableLocations.length > 0 && (
      <div className="px-4 py-2" style={{ ...mutedText, ...rowBorder, fontSize: 12 }} data-testid="staffing-unavailable">
        {`Shifts unavailable for ${unavailableLocations.join(', ')} — that Homebase plan doesn't include API access.`}
      </div>
    )}
  </>
);

const StaffingTimeline = ({ staffing, date, isToday }) => {
  const now = useNow(isToday);
  const {
    shifts, totals, timeZone, unavailableLocations, pendingLocations, locationNames, opendate,
  } = staffing;
  const nowHour = wallClockHour(now, date, timeZone);
  // Opendate shifts carry a clock-in status; Homebase shifts only have their scheduled times.
  const isOnNow = (shift) => isToday && !shift.open && (shift.status
    ? shift.status === 'clocked_in'
    : now >= Date.parse(shift.startAt) && now < Date.parse(shift.endAt));

  const rows = staffRows(shifts);
  const { departments, colorFor } = departmentColors(shifts);
  const firstHour = shifts.length ? Math.floor(Math.min(...shifts.map((s) => s.startHour))) : 9;
  const lastHour = shifts.length ? Math.ceil(Math.max(...shifts.map((s) => s.endHour))) : 17;
  const hours = Array.from({ length: Math.max(lastHour - firstHour, 1) }, (_, i) => firstHour + i);
  const trackWidth = hours.length * HOUR_WIDTH;
  const onNowCount = new Set(shifts.filter(isOnNow).map((s) => s.personId || s.id)).size;
  const showNowLine = isToday && nowHour >= firstHour && nowHour <= lastHour;
  const scroller = useRef(null);
  // An overnight shift starts the track hours before now; open it at the now-line, once.
  useEffect(() => {
    if (showNowLine && scroller.current) scroller.current.scrollLeft = Math.max((nowHour - firstHour - 1) * HOUR_WIDTH, 0);
  }, []);

  const summary = [
    `${totals.people} ${totals.people === 1 ? 'person' : 'people'}`,
    `${totals.hours.toLocaleString()} hrs`,
    isToday ? `${onNowCount} on shift now` : null,
  ].filter(Boolean).join(' · ');

  let empty = null;
  if (rows.length === 0) {
    if (opendate) {
      empty = { title: 'No shifts scheduled', description: 'Published shifts for this day will show up here.' };
    } else if (locationNames.length > 0) {
      empty = { title: 'No shifts scheduled', description: 'Published Homebase shifts for this day will show up here.' };
    } else if (pendingLocations.length > 0) {
      empty = { title: 'Syncing shifts', description: 'Shifts from Homebase will show up here within a few minutes.' };
    } else {
      empty = { title: 'Shifts unavailable', description: "Homebase isn't sharing shifts for these locations." };
    }
  }

  return (
    <Card
      header={(
        <CardHeader
          title={isToday ? 'Staff on today' : 'Staff scheduled'}
          supportingText={`${summary}${locationNames.length ? ` · from Homebase (${locationNames.join(', ')})` : ''}`}
        />
      )}
      padding="none"
    >
      {empty ? (
        <EmptyState size="sm" title={empty.title} description={empty.description} />
      ) : (
        <div ref={scroller} style={{ overflowX: 'auto' }} data-testid="staffing-timeline">
          <div style={{ minWidth: NAME_COLUMN_WIDTH + trackWidth, position: 'relative' }}>
            <div className="d-flex" style={{ borderBottom: '1px solid var(--sl-border-secondary)' }}>
              <div
                className="px-4 py-2 font-weight-bold"
                style={{ width: NAME_COLUMN_WIDTH, flexShrink: 0, position: 'sticky', left: 0, zIndex: 2, background: 'var(--sl-bg-primary)', ...mutedText }}
              >
                Staff
              </div>
              {hours.map((hour) => (
                <div key={hour} className="py-2 pl-1" style={{ width: HOUR_WIDTH, flexShrink: 0, borderLeft: '1px solid var(--sl-border-secondary)', ...mutedText, fontSize: 12 }}>
                  {hourLabel(hour)}
                </div>
              ))}
            </div>
            {rows.map((row, index) => {
              const rowOnNow = row.shifts.some(isOnNow);
              const statusBadge = rowOnNow ? null : row.shifts.map((s) => STAFF_STATUS_BADGES[s.status]).findLast(Boolean);
              const notes = row.shifts.filter((s) => s.note);
              return (
                <div key={row.key} data-testid="staffing-row" style={index === 0 ? undefined : rowBorder}>
                  <div className="d-flex">
                    <div
                      className="d-flex align-items-center px-4 py-2"
                      style={{
                        gap: 8,
                        width: NAME_COLUMN_WIDTH,
                        flexShrink: 0,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        background: 'var(--sl-bg-primary)',
                        fontWeight: rowOnNow ? 700 : 500,
                        fontStyle: row.open ? 'italic' : undefined,
                        color: row.open ? 'var(--sl-text-tertiary)' : 'var(--sl-text-primary)',
                      }}
                    >
                      {row.initials && <span aria-hidden style={{ display: 'inline-flex' }}><Avatar size="xs" initials={row.initials} alt={row.name} /></span>}
                      <span className="text-truncate">
                        {row.name}
                        {rowOnNow && <span style={visuallyHidden}> (on shift now)</span>}
                      </span>
                      {statusBadge && (
                        <Badge color={statusBadge.color} size="sm" data-testid="staffing-status" style={{ flexShrink: 0 }}>{statusBadge.label}</Badge>
                      )}
                    </div>
                    <div className="position-relative" style={{ width: trackWidth, height: 40, flexShrink: 0 }}>
                      {row.shifts.map((shift) => (
                        <ShiftBar key={shift.id} shift={shift} firstHour={firstHour} onNow={isOnNow(shift)} colorFor={colorFor} />
                      ))}
                    </div>
                  </div>
                  {notes.length > 0 && (
                    <div className="d-flex pb-2">
                      <div className="d-none d-md-block" style={{ width: NAME_COLUMN_WIDTH, flexShrink: 0 }} />
                      <div
                        className="px-4 px-md-0"
                        style={{ ...mutedText, fontSize: 12, position: 'sticky', left: 0, width: 'max-content', maxWidth: 'min(560px, 85vw)' }}
                        data-testid="staffing-note"
                      >
                        {notes.map((s) => (
                          <div key={s.id} className="d-flex" style={{ gap: 6 }}>
                            <MessageTextSquare01 width={12} height={12} aria-hidden style={{ flexShrink: 0, marginTop: 3 }} />
                            <span>{`${s.startLabel}: ${s.note}`}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {showNowLine && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: NAME_COLUMN_WIDTH + (nowHour - firstHour) * HOUR_WIDTH,
                  width: 2,
                  background: 'var(--sl-fg-error)',
                  opacity: 0.6,
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
        </div>
      )}
      {!empty && <StaffingLegend departments={departments} colorFor={colorFor} />}
      <StaffingFootnotes unavailableLocations={unavailableLocations} pendingLocations={pendingLocations} />
    </Card>
  );
};

const goTo = (path) => window.location.assign(path);

const DayNavigation = ({ isToday, todayPath, previousDayPath, nextDayPath }) => (
  <div className="d-flex" style={{ gap: 8 }}>
    {previousDayPath && (
      <Button variant="secondary" size="sm" icon={ChevronLeft} aria-label="Previous day" onClick={() => goTo(previousDayPath)} />
    )}
    {!isToday && <Button variant="secondary" size="sm" onClick={() => goTo(todayPath)}>Today</Button>}
    <Button variant="secondary" size="sm" icon={ChevronRight} aria-label="Next day" onClick={() => goTo(nextDayPath)} />
  </div>
);

const TodayApp = ({
  date, isToday, teamName, currency, calendarPath, todayPath, previousDayPath, nextDayPath,
  summary, events, recentScans, weather, notes, staffing,
}) => {
  const moneyFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 });
  const formatMoney = (amount) => moneyFormatter.format(amount);
  const day = new Date(`${date}T00:00:00`);
  const formattedDate = day.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  const dayName = isToday ? 'Today' : day.toLocaleDateString(undefined, { weekday: 'long' });
  const supportingText = `${formattedDate} · ${summary.eventCount} event${summary.eventCount === 1 ? '' : 's'} scheduled`;

  return (
    <div className="container-fluid py-4" style={cardBorder}>
      <PageHeader
        title={`${dayName} at ${teamName}`}
        supportingText={supportingText}
        actions={(
          <DayNavigation
            isToday={isToday}
            todayPath={todayPath}
            previousDayPath={previousDayPath}
            nextDayPath={nextDayPath}
          />
        )}
      />
      <div className="mt-4">
        <SummaryMetrics summary={summary} isToday={isToday} formatMoney={formatMoney} />
      </div>
      {staffing && (
        <div className="mb-3">
          <StaffingTimeline staffing={staffing} date={date} isToday={isToday} />
        </div>
      )}
      <div className="row mx-n2">
        <div className="col-lg-8 px-2 mb-3">
          <Card
            header={<CardHeader title={isToday ? "Today's schedule" : 'Schedule'} actions={<a href={calendarPath}>View calendar →</a>} />}
            padding="none"
          >
            {events.length === 0 ? (
              <EmptyState size="sm" title="Nothing scheduled" description="Events starting on this day will show up here." />
            ) : events.map((event, index) => (
              <ScheduleRow key={event.id} event={event} isFirst={index === 0} formatMoney={formatMoney} />
            ))}
          </Card>
          {events.length > 0 && (
            <div className="mt-3">
              <Card header={<CardHeader title="Door activity" />} padding="none">
                {events.map((event, index) => <DoorActivityRow key={event.id} event={event} isFirst={index === 0} />)}
                {recentScans.length > 0 && <RecentScans scans={recentScans} />}
              </Card>
            </div>
          )}
        </div>
        <div className="col-lg-4 px-2">
          {weather.length > 0 && (
            <div className="mb-3">
              <WeatherCard weather={weather} />
            </div>
          )}
          <NotesCard notes={notes} />
        </div>
      </div>
    </div>
  );
};

export default TodayApp;
